var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box, makeStyles, useTheme } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useBlockLayout, useColumnOrder, usePagination, useSortBy, useTable, } from "react-table";
import { useSticky } from "react-table-sticky";
import { SortIcon } from "../../icons";
import FTLLoader from "../../loaders/FTLLoader";
import FTLTypography from "../FTLTypography";
import FTLTableBody from "./FTLTableBody";
import FTLTableCell from "./FTLTableCell";
import FTLTableFooter from "./FTLTableFooter";
import FTLTableRow from "./FTLTableRow";
export default (_a) => {
    var { showHeader = true, showFooter = true, noCellBorder = false, columns, data, isFetching, hasData = Boolean(data === null || data === void 0 ? void 0 : data.length), isSearching, pageCount = 0, rowHeight = 40, noDataMessage = (React.createElement(FTLTypography, { variant: "h2", color: "secondary" }, "\u041D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445 \u0434\u043B\u044F \u043E\u0442\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u044F")), noFilteredDataMessage = (React.createElement(FTLTypography, { variant: "h2", color: "secondary" }, "\u041F\u043E\u0438\u0441\u043A \u043D\u0435 \u0434\u0430\u043B \u0440\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442\u043E\u0432")), rowDisable, onRowClick, tableState, actionsSize = 1, actionAccessor = "action", setTableState, rowProps, classes: classesFromProps } = _a, otherProps = __rest(_a, ["showHeader", "showFooter", "noCellBorder", "columns", "data", "isFetching", "hasData", "isSearching", "pageCount", "rowHeight", "noDataMessage", "noFilteredDataMessage", "rowDisable", "onRowClick", "tableState", "actionsSize", "actionAccessor", "setTableState", "rowProps", "classes"]);
    const tableData = useMemo(() => { var _a; return (!hasData ? Array((_a = tableState === null || tableState === void 0 ? void 0 : tableState.pageSize) !== null && _a !== void 0 ? _a : 5).fill({}) : data); }, [hasData, data]);
    const tableColumns = useMemo(() => isFetching && !hasData
        ? columns.map((column) => (Object.assign(Object.assign({}, column), { type: "skeleton", Cell: (column === null || column === void 0 ? void 0 : column.accessor) === actionAccessor ? (React.createElement(Box, { display: "flex", justifyContent: "center", alignItems: "center", mt: 1, ml: -2 }, Array(actionsSize).fill(React.createElement("div", { style: { marginLeft: 8 } },
                React.createElement(Skeleton, { variant: "circle", width: "24px", height: "24px" }))))) : (React.createElement(Skeleton, { variant: "text" })) })))
        : columns, [columns, isFetching, hasData]);
    const { getTableProps, getTableBodyProps, prepareRow, setColumnOrder, setHiddenColumns, rows, headerGroups, state, } = useTable({
        autoResetPage: true,
        columns: tableColumns,
        data: tableData,
        defaultCanSort: false,
        disableMultiSort: false,
        manualPagination: true,
        pageCount,
        initialState: {
            pageSize: tableState === null || tableState === void 0 ? void 0 : tableState.pageSize,
            pageIndex: tableState === null || tableState === void 0 ? void 0 : tableState.pageIndex,
            hiddenColumns: (tableState === null || tableState === void 0 ? void 0 : tableState.hiddenColumns) || [],
            columnOrder: tableState === null || tableState === void 0 ? void 0 : tableState.columnOrder,
        },
    }, useSortBy, usePagination, useColumnOrder, useBlockLayout, useSticky);
    useEffect(() => {
        if ((tableState === null || tableState === void 0 ? void 0 : tableState.columnOrder) && tableState.columnOrder !== state.columnOrder)
            setColumnOrder(tableState.columnOrder);
    }, [tableState === null || tableState === void 0 ? void 0 : tableState.columnOrder]);
    useEffect(() => {
        if ((tableState === null || tableState === void 0 ? void 0 : tableState.hiddenColumns) &&
            tableState.hiddenColumns !== state.hiddenColumns)
            setHiddenColumns(tableState.hiddenColumns);
    }, [tableState === null || tableState === void 0 ? void 0 : tableState.hiddenColumns]);
    const tableRef = useRef(null);
    const [showFirstColumnShadow, setShowFirstColumnShadow] = useState(false);
    const [showLastColumnShadow, setShowLastColumnShadow] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const columnsScrollHandler = (obj) => {
        if (obj.scrollLeft === 0)
            setShowFirstColumnShadow(false);
        else
            setShowFirstColumnShadow(true);
        if (obj.scrollLeft === obj.scrollWidth - obj.offsetWidth)
            setShowLastColumnShadow(false);
        else
            setShowLastColumnShadow(true);
    };
    useEffect(() => {
        if (hasData && !isFetching)
            setIsLoading(false);
    }, [hasData, isFetching]);
    const classes = useStyles({
        showFooter,
        onRowClick,
        hasData,
        isFetching,
        showLastColumnShadow,
    });
    const theme = useTheme();
    const rowComponents = useMemo(() => {
        return rows.map((row) => {
            prepareRow(row);
            const isDisabled = row.isDisabled || (rowDisable && rowDisable(row));
            const props = rowProps && rowProps(row);
            const canInteract = !isFetching && hasData && onRowClick;
            return (React.createElement(FTLTableRow, Object.assign({}, row.getRowProps(), props, { key: row.id, className: clsx(classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.row, canInteract && classes.rowHoverStyle), noCellBorder: noCellBorder, "data-disabled": isDisabled, onClick: (e) => {
                    var _a;
                    if (
                    // !isDisabled &&
                    !isFetching &&
                        hasData &&
                        onRowClick &&
                        !((_a = document.getSelection()) === null || _a === void 0 ? void 0 : _a.toString()))
                        onRowClick(row, e);
                } }), row.cells.map((cell, cellIndex) => {
                var _a;
                const widthProps = typeof cell.column.width === "number"
                    ? {
                        minWidth: cell.column.width ||
                            cell.column.minWidth ||
                            cell.column.maxWidth,
                    }
                    : {
                        width: cell.column.width,
                        minWidth: cell.column.minWidth,
                        maxWidth: cell.column.maxWidth,
                    };
                return (React.createElement(FTLTableCell, Object.assign({}, cell.getCellProps(), { isDisabled: typeof cell.value === "object" ? false : isDisabled, key: row.id + cellIndex, noCellBorder: noCellBorder, tabIndex: -1, textAlign: ((_a = cell.column) === null || _a === void 0 ? void 0 : _a.align) || "left" }, widthProps, { className: clsx(cell.column.id === actionAccessor && classes.actionCell, cell.column.showOnHover && classes.showOnHover, classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.cell), height: cell.column.showOnHover ? rowHeight : undefined }), cell.value !== undefined ||
                    cell.column.type === "skeleton" ||
                    (typeof cell.column.Cell === "function" &&
                        (cell.column.Cell.name === "" ||
                            cell.column.Cell.name === "Cell"))
                    ? cell.render("Cell")
                    : "—"));
            })));
        });
    }, [rows, rowHeight, prepareRow, rowDisable, onRowClick, rowProps]);
    const headerComponents = useMemo(() => showHeader &&
        headerGroups.map((headerGroup) => {
            return (React.createElement(FTLTableRow, Object.assign({ type: "head" }, headerGroup.getHeaderGroupProps(), { key: headerGroup.id, className: clsx(classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.headRow, classes.headRow) }), headerGroup.headers.map((column, index) => {
                var _a;
                const _b = column.getHeaderProps(column.getSortByToggleProps({ title: "" })), { onClick } = _b, otherHeadProps = __rest(_b, ["onClick"]);
                const widthProps = typeof column.width === "number"
                    ? {
                        minWidth: column.width || column.minWidth || column.maxWidth,
                    }
                    : {
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                    };
                return (React.createElement(FTLTableCell, Object.assign({ type: "head", key: column.id, textAlign: (_a = column === null || column === void 0 ? void 0 : column.align) !== null && _a !== void 0 ? _a : "left", isFocusable: column.canSort && tableState, tabIndex: column.canSort && tableState ? index : -1 }, widthProps, otherHeadProps, { className: clsx(column.showOnHover && classes.showOnHover, classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.headCell), onClick: () => {
                        if (column.canSort && setTableState && tableState)
                            if (tableState.sortName === column.id &&
                                tableState.sortDirection === "DESC")
                                setTableState({
                                    sortDirection: "ASC",
                                    sortName: column.id,
                                });
                            else if (tableState.sortName === column.id &&
                                tableState.sortDirection === "ASC")
                                setTableState({
                                    sortDirection: undefined,
                                    sortName: undefined,
                                });
                            else
                                setTableState({
                                    sortDirection: "DESC",
                                    sortName: column.id,
                                });
                    } }),
                    React.createElement(Box, { display: column.canSort ? "inline-flex" : "block", overflow: "hidden", flexDirection: column.canSort && (column === null || column === void 0 ? void 0 : column.reverse) ? "row-reverse" : "row" },
                        column.render("Header"),
                        column.canSort && tableState ? (React.createElement(SortIcon, { sort: tableState.sortName === column.id
                                ? tableState.sortDirection
                                : undefined })) : null)));
            })));
        }), [headerGroups, showHeader, tableState]);
    if (hasData || isFetching) {
        return (React.createElement(Box, Object.assign({ className: classes.root, position: "relative" }, otherProps, { height: "100%" }),
            isFetching && hasData && (React.createElement(FTLLoader, { alignItems: "center", justifyContent: "center", height: showFooter ? "calc(100% - 80px)" : "100%", width: "100%", position: "absolute", zIndex: theme.zIndex.tooltip - 1 })),
            React.createElement("div", { className: clsx(classes.overflowTableWrapper, showFirstColumnShadow && classes.showFirstColumnShadow, showLastColumnShadow && classes.showLastColumnShadow), ref: tableRef, onScroll: (e) => {
                    columnsScrollHandler(e.currentTarget);
                } },
                React.createElement("table", Object.assign({}, getTableProps(), { className: clsx(classes.table, classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.table) }),
                    headerComponents,
                    React.createElement(FTLTableBody, Object.assign({}, getTableBodyProps(), { showHeader: showHeader, style: isFetching && hasData ? { opacity: 0.5 } : {}, className: classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.tableBody }), rowComponents))),
            !isLoading && showFooter && tableState && (React.createElement(FTLTableFooter, { disabled: isFetching, pageIndex: tableState.pageIndex, pageSize: tableState.pageSize, pageCount: pageCount, setPageSize: (pageSize) => setTableState && setTableState({ pageSize }), gotoPage: (pageNumber) => {
                    setTableState && setTableState({ pageIndex: pageNumber - 1 });
                }, className: classesFromProps === null || classesFromProps === void 0 ? void 0 : classesFromProps.footer }))));
    }
    else {
        return (React.createElement("div", { className: classes.noDataMessage }, isSearching && !hasData ? noFilteredDataMessage : noDataMessage));
    }
};
const useStyles = makeStyles((theme) => ({
    root: {
        overflow: "hidden",
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": {
            width: 0,
        },
    },
    actionCell: {
        paddingTop: 2,
        paddingBottom: 2,
        minHeight: 0,
        "& .MuiIconButton-root": {
            padding: 0,
        },
    },
    overflowTableWrapper: {
        display: "block",
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: "100%",
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    noDataMessage: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        flexDirection: "column",
        height: 322,
    },
    headRow: {
        zIndex: 4,
        position: "absolute",
        top: 0,
        background: "#ffffff",
        width: "100%",
    },
    showOnHover: {
        "&[data-sticky-td], &td": {
            display: "flex",
            alignItems: "center",
            width: "auto",
            height: "100%",
            position: "absolute !important",
            opacity: 0,
            "&:hover:not(th), &:focus:not(th)": {
                opacity: 1,
            },
        },
        "&[data-sticky-first-right-td]:not(th)::before": {
            content: "''",
            position: "absolute",
            display: "block",
            top: 0,
            left: -12,
            width: 12,
            height: "100%",
            background: `linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, ${theme.palette.cell.default} 100%)`,
        },
        "&[data-sticky-last-left-td]:not(th)::after": {
            content: "''",
            position: "absolute",
            display: "block",
            top: 0,
            right: -12,
            width: 12,
            height: "100%",
            background: `linear-gradient(270deg, ${theme.palette.cell.default} 0%, rgba(255, 255, 255, 0) 100%)`,
        },
    },
    showLastColumnShadow: {
        "& [data-sticky-first-right-td]:not($showOnHover)": {
            boxShadow: `inset 1px 0 0 0 ${theme.palette.border.menuContainer}`,
        },
    },
    showFirstColumnShadow: {
        "& [data-sticky-last-left-td]:not($showOnHover)": {
            boxShadow: `inset -1px 0 0 0 ${theme.palette.border.menuContainer}`,
        },
    },
    list: {
        overflow: "hidden auto !important",
    },
    rowHoverStyle: {
        cursor: "pointer",
        "& [data-sticky-first-right-td]:not(th)::before": {
            background: `linear-gradient(270deg, ${theme.palette.cell.tableHover} 0%, rgba(255, 255, 255, 0) 100%)`,
        },
        "& [data-sticky-last-left-td]:not(th)::after": {
            background: `linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, ${theme.palette.cell.tableHover} 100%)`,
        },
        "&:hover td, &:focus td": {
            backgroundColor: theme.palette.cell.tableHover,
        },
    },
    rowWOFooter: {
        "&:last-child": {
            borderBottom: "none !important",
        },
    },
    table: {
        borderCollapse: "collapse",
        borderSpacing: 0,
        width: "100%",
        "& tr:hover td$showOnHover, & tr:focus td$showOnHover": {
            opacity: 1,
        },
    },
}));
//# sourceMappingURL=FTLTable.js.map